import { WMSLayer } from "./layer/WMSLayer.js";
import { WFSLayer } from "./layer/WFSLayer.js";
import { Layer } from "./layer/Layer.js";
import { GeoJSONLayer } from "./layer/GeoJSONLayer.js";
import { EventObject } from "../events/EventObject.js";
import { TileLayer } from "./layer/TileLayer.js";
import { DwsLayer } from "./layer/DwsLayer.js";
import { CsvLayer } from "./layer/CsvLayer.js";
import { OpusLayer } from "./layer/OpusLayer.js";
import { IndexBinder } from "./importer/binder/IndexBinder.js";
import { Folder } from "./layer/Folder.js";
import { FilterLayer } from "./layer/FilterLayer.js";
import { OgcBinder } from "./importer/binder/OgcBinder.js";

export { LayerCache };

/**
 * LayerCache class for getting and handling layers
 * 
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @author awalter <andreas.walter@awi.de>
 * @author rhess <robin.hess@awi.de>
 * 
 * @memberof vef.map
 */
class LayerCache extends EventObject {

    static layerClasses = {
        "wms": WMSLayer,
        "wfs": WFSLayer,
        "geojson": GeoJSONLayer,
        "dws": DwsLayer,
        "csv": CsvLayer,
        "opus": OpusLayer,
        "tile": TileLayer,
        "folder": Folder,
        "filter": FilterLayer
    }

    /**
     * Creates an empty LayerCache instance to which Layers can be added
     * using a maps-project configuration or manually using the
     * method addLayerToCache.
     */
    constructor() {
        super({
            "remove_layer": [],
            "add_layer": [],
            "filter_change": []
        });

        this._layerCache = {};
        this._filterTimeout = null;
    }

    /**
     * Static method to load a layer from a json config
     * @param {object} config 
     * @returns {Layer} loaded layer instance
     */
    static loadLayer(config, cache, id) {
        const type = (config.type || cache.type || "").toLowerCase();
        const LayerClass = this.layerClasses[type] || Folder;
        return new LayerClass(config, cache, id);
    }

    resolveCatalogLayers_(layers) {
        return new Promise(resolve => {
            const catalogIds = [];
            for (let id in layers) {
                if ("catalogId" in layers[id]) catalogIds.push(layers[id].catalogId);
            }
            if (catalogIds.length == 0) {
                resolve({})
            } else {
                new IndexBinder().fetchLayersByIds(catalogIds)
                    .then(layers => resolve(layers))
                    .catch(() => resolve({}));
            }
        });
    }

    /**
     * Add layer to the LayerCache using a syntax that is 
     * compatible to the Layer's constructor.
     * 
     * @param {object[]} layers layer configs
     * @param {object[]} cache cached layer configs
     * 
     * @returns {Promise} resolved layers
     */
    loadLayers(layers, cache) {
        return new Promise((resolve) => {
            this.resolveCatalogLayers_(layers).then(catalogLayers => {
                for (let id in layers) {
                    let cachedLayer = (id in cache) ? cache[id] : {};
                    if (("catalogId" in layers[id]) && (layers[id].catalogId in catalogLayers)) {
                        cachedLayer = catalogLayers[layers[id].catalogId];
                    }
                    this.addLayerToCache(LayerCache.loadLayer(layers[id], cachedLayer, id));
                }

                for (let i = 0; i < layers.length; ++i) {

                }
                resolve();
            });
        });
    }

    /**
     * Add an existing Layer to the LayerCache
     * 
     * @param {Object} layer
     */
    addLayerToCache(layer) {
        this._layerCache[layer.uniqueId] = layer;
        if (layer instanceof Layer) {
            layer.on("remove", () => this.fire("remove_layer", layer));
        }
        if (layer instanceof FilterLayer) this.initFilterLayer_(layer);
        this.fire("add_layer", layer);
    }

    /**
     * Method that returns a layer from LayerCache based on UniqueId provided
     * 
     * @param {string} id 
     */
    getLayerById(id) {
        if (this._layerCache.hasOwnProperty(id)) {
            return this._layerCache[id];
        }
    }

    /**
     * Reloads the metadata of all layers from the given service identified by url and type
     * @param {string} url 
     * @param {string} type 
     */
    reloadServiceMetadata(url, type) {
        OgcBinder.getCapabilities(null, url, type).then(data => {
            this.forEach(layer => {
                if ((url == layer.serviceUrl) && (type == layer.type)) {
                    for (let i = 0; i < data.length; ++i) {
                        for (let id in data[i].layers) {
                            if (data[i].layers[id].name == layer.name) {
                                layer.cache = data[i].layers[id].cache;
                                layer.fire("update", layer);
                            }
                        }
                    }
                }
            });
        })
    }

    /**
     * Reloads the metadata of all layers
     * @param {string} url 
     * @param {string} type 
     */
    reloadAllServiceMetadata() {
        const reloaded = { wms: [], wfs: [] };
        this.forEach(layer => {
            if (layer instanceof WMSLayer || layer instanceof WFSLayer) {
                const type = layer.type.toLowerCase();
                if (!reloaded[type].includes(layer.serviceUrl)) {
                    reloaded[type].push(layer.serviceUrl);
                    this.reloadServiceMetadata(layer.serviceUrl, type);
                }
            }
        });
    }

    /**
     * Iterate over each layer inside the LayerCache
     * 
     * @param {function} callback
     */
    forEach(callback) {
        for (let i in this._layerCache) {
            callback(this._layerCache[i]);
        }
    }

    /**
     * Clear layercache and dispose layers
     */
    dispose() {
        this.forEach(l => l.dispose());
        this._layerCache = {};
    }

    /**
     * @private
     */
    initFilterLayer_(layer) {
        const applyFilter = () => {
            if (this._filterTimeout) clearTimeout(this._filterTimeout);
            this._filterTimeout = setTimeout(() => {
                this.fire("filter_change", this);
                this._filterTimeout = null;
            }, 100);
        };
        layer.on("enable", applyFilter);
        layer.on("disable", applyFilter);
    }

    /**
     * Get selected FilterLayers
     * @returns {object[]} filters
     */
    getActiveFilter() {
        const mapping = {};
        const filters = [];

        this.forEach(layer => {
            if ((layer instanceof FilterLayer) && layer.active) {
                if (!(layer.targetLayer in mapping)) mapping[layer.targetLayer] = {};
                if (!(layer.column in mapping[layer.targetLayer])) {
                    const excluded = Object.keys(this._layerCache);
                    const index = excluded.indexOf(layer.targetLayer);
                    if (index > -1) excluded.splice(index, 1);
                    const f = {
                        excludedLayers: excluded,
                        column: layer.column,
                        values: [layer.values]
                    };
                    mapping[layer.targetLayer][layer.column] = f;
                    filters.push(f);
                } else {
                    const f = mapping[layer.targetLayer][layer.column];
                    f.values.push(layer.values);
                }
            }
        })

        return filters;
    }
}